// Medical Scores Database - Part 2: Extended Collection
// More specialized scores across all medical specialties

import {
  Brain,
  Heart,
  Wind,
  Activity,
  Baby,
  Stethoscope,
  Thermometer,
  Droplets,
  Flame,
  Syringe,
  Pill,
  Bone,
  Eye,
  Scissors,
  Scale,
  Clock,
  AlertTriangle,
  Calculator,
  Beaker,
  User,
  Users,
  Zap,
  Target,
  TrendingUp,
  Shield,
  Gauge
} from 'lucide-react';

// ==================== MÉDECINE D'URGENCE ====================
export const EMERGENCY_SCORES = {
  mgap: {
    name: 'Score MGAP',
    category: 'Urgences',
    icon: Stethoscope,
    description: 'Gravité polytraumatisé en pré-hospitalier',
    color: 'orange',
    fields: [
      { id: 'mechanism', label: 'Mécanisme', type: 'select', options: [
        { value: 4, label: 'Non pénétrant (4 pts)' },
        { value: 0, label: 'Pénétrant (0 pt)' }
      ]},
      { id: 'gcs', label: 'Glasgow', type: 'select', options: [
        { value: 0, label: 'GCS 3-8 (0 pt)' },
        { value: 2, label: 'GCS 9-12 (2 pts)' },
        { value: 4, label: 'GCS 13-15 (4 pts)' }
      ]},
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 5, label: '< 60 ans (5 pts)' },
        { value: 0, label: '≥ 60 ans (0 pt)' }
      ]},
      { id: 'sbp', label: 'PAS', type: 'select', options: [
        { value: 0, label: '< 60 mmHg (0 pt)' },
        { value: 3, label: '60-120 mmHg (3 pts)' },
        { value: 5, label: '> 120 mmHg (5 pts)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 18 };
    },
    interpret: (score) => {
      if (score >= 23) return { level: 'success', text: 'Risque faible', detail: 'Mortalité < 5%' };
      if (score >= 18) return { level: 'warning', text: 'Risque intermédiaire', detail: 'Mortalité 5-15%' };
      return { level: 'danger', text: 'Risque élevé', detail: 'Mortalité > 30%. Trauma center niveau 1' };
    },
    formula: 'MGAP = Mécanisme + GCS + Âge + PAS'
  },

  vittel: {
    name: 'Critères de Vittel',
    category: 'Urgences',
    icon: Stethoscope,
    description: 'Triage traumatisés graves',
    color: 'orange',
    fields: [
      { id: 'ejection', label: 'Éjection véhicule', type: 'checkbox', points: 1 },
      { id: 'death', label: 'Autre passager décédé', type: 'checkbox', points: 1 },
      { id: 'fall', label: 'Chute > 6m', type: 'checkbox', points: 1 },
      { id: 'pedestrian', label: 'Piéton/2 roues renversé', type: 'checkbox', points: 1 },
      { id: 'deformation', label: 'Déformation véhicule', type: 'checkbox', points: 1 },
      { id: 'speed', label: 'Vitesse > 60 km/h', type: 'checkbox', points: 1 },
      { id: 'blast', label: 'Blast', type: 'checkbox', points: 1 },
      { id: 'gcs', label: 'GCS < 13', type: 'checkbox', points: 1 },
      { id: 'sbp', label: 'PAS < 90 mmHg', type: 'checkbox', points: 1 },
      { id: 'spo2', label: 'SpO₂ < 90%', type: 'checkbox', points: 1 },
      { id: 'trauma', label: 'Traumatisme pénétrant', type: 'checkbox', points: 1 },
      { id: 'burn', label: 'Brûlure grave', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 12 };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: 'Vittel négatif', detail: 'Pas de critère de gravité' };
      return { level: 'danger', text: `Vittel positif (${score} critère${score > 1 ? 's' : ''})`, detail: 'Traumatisé grave: régulation SAMU, trauma center' };
    },
    formula: 'Vittel positif si ≥ 1 critère présent'
  },

  shockIndex: {
    name: 'Shock Index',
    category: 'Urgences',
    icon: Activity,
    description: 'Gravité état de choc',
    color: 'orange',
    fields: [
      { id: 'hr', label: 'Fréquence cardiaque (bpm)', type: 'number', min: 30, max: 250 },
      { id: 'sbp', label: 'PAS (mmHg)', type: 'number', min: 40, max: 300 }
    ],
    calculate: (values) => {
      const hr = values.hr || 80;
      const sbp = values.sbp || 120;
      const si = (hr / sbp).toFixed(2);
      return { score: parseFloat(si), max: '' };
    },
    interpret: (score) => {
      if (score < 0.6) return { level: 'success', text: `SI: ${score}`, detail: 'Normal' };
      if (score < 0.9) return { level: 'success', text: `SI: ${score}`, detail: 'Normal ou légèrement élevé' };
      if (score < 1.4) return { level: 'warning', text: `SI: ${score}`, detail: 'Choc probable. Remplissage, rechercher hémorragie' };
      return { level: 'danger', text: `SI: ${score}`, detail: 'Choc sévère. Transfusion probable, bloc opératoire' };
    },
    formula: 'Shock Index = FC / PAS (normal 0.5-0.7)'
  },

  abc: {
    name: 'Score ABC Transfusion',
    category: 'Urgences',
    icon: Droplets,
    description: 'Besoin transfusion massive',
    color: 'orange',
    fields: [
      { id: 'penetrating', label: 'Traumatisme pénétrant', type: 'checkbox', points: 1 },
      { id: 'sbp', label: 'PAS ≤ 90 mmHg', type: 'checkbox', points: 1 },
      { id: 'hr', label: 'FC ≥ 120/min', type: 'checkbox', points: 1 },
      { id: 'fast', label: 'FAST positif', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 4 };
    },
    interpret: (score) => {
      if (score <= 1) return { level: 'success', text: `ABC ${score}`, detail: 'Transfusion massive peu probable' };
      return { level: 'danger', text: `ABC ${score}`, detail: 'Transfusion massive probable. Activer protocole' };
    },
    formula: 'ABC = Pénétrant + PAS≤90 + FC≥120 + FAST+'
  },

  iss: {
    name: 'ISS (Injury Severity Score)',
    category: 'Urgences',
    icon: Stethoscope,
    description: 'Gravité anatomique polytraumatisme',
    color: 'orange',
    fields: [
      { id: 'head', label: 'Tête/Cou (AIS)', type: 'select', options: [
        { value: 0, label: '0 - Pas de lésion' }, { value: 1, label: '1 - Mineure' },
        { value: 2, label: '2 - Modérée' }, { value: 3, label: '3 - Sérieuse' },
        { value: 4, label: '4 - Sévère' }, { value: 5, label: '5 - Critique' }
      ]},
      { id: 'face', label: 'Face (AIS)', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' },
        { value: 3, label: '3' }, { value: 4, label: '4' }, { value: 5, label: '5' }
      ]},
      { id: 'thorax', label: 'Thorax (AIS)', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' },
        { value: 3, label: '3' }, { value: 4, label: '4' }, { value: 5, label: '5' }
      ]},
      { id: 'abdomen', label: 'Abdomen (AIS)', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' },
        { value: 3, label: '3' }, { value: 4, label: '4' }, { value: 5, label: '5' }
      ]},
      { id: 'extremity', label: 'Extrémités (AIS)', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' },
        { value: 3, label: '3' }, { value: 4, label: '4' }, { value: 5, label: '5' }
      ]},
      { id: 'external', label: 'Externe (AIS)', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' },
        { value: 3, label: '3' }, { value: 4, label: '4' }, { value: 5, label: '5' }
      ]}
    ],
    calculate: (values) => {
      const scores = [values.head || 0, values.face || 0, values.thorax || 0, 
                      values.abdomen || 0, values.extremity || 0, values.external || 0];
      scores.sort((a, b) => b - a);
      const iss = Math.pow(scores[0], 2) + Math.pow(scores[1], 2) + Math.pow(scores[2], 2);
      return { score: iss, max: 75 };
    },
    interpret: (score) => {
      if (score < 9) return { level: 'success', text: `ISS ${score}`, detail: 'Traumatisme mineur' };
      if (score < 16) return { level: 'warning', text: `ISS ${score}`, detail: 'Traumatisme modéré' };
      if (score < 25) return { level: 'danger', text: `ISS ${score}`, detail: 'Traumatisme sévère' };
      return { level: 'danger', text: `ISS ${score}`, detail: 'Polytraumatisme critique. Mortalité > 25%' };
    },
    formula: 'ISS = Somme des carrés des 3 AIS les plus élevés (max 75)'
  },

  rts: {
    name: 'RTS (Revised Trauma Score)',
    category: 'Urgences',
    icon: Stethoscope,
    description: 'Gravité physiologique traumatisme',
    color: 'orange',
    fields: [
      { id: 'gcs', label: 'Glasgow', type: 'select', options: [
        { value: 4, label: '13-15 (4)' }, { value: 3, label: '9-12 (3)' },
        { value: 2, label: '6-8 (2)' }, { value: 1, label: '4-5 (1)' }, { value: 0, label: '3 (0)' }
      ]},
      { id: 'sbp', label: 'PAS (mmHg)', type: 'select', options: [
        { value: 4, label: '> 89 (4)' }, { value: 3, label: '76-89 (3)' },
        { value: 2, label: '50-75 (2)' }, { value: 1, label: '1-49 (1)' }, { value: 0, label: '0 (0)' }
      ]},
      { id: 'rr', label: 'Fréquence respiratoire', type: 'select', options: [
        { value: 4, label: '10-29 (4)' }, { value: 3, label: '> 29 (3)' },
        { value: 2, label: '6-9 (2)' }, { value: 1, label: '1-5 (1)' }, { value: 0, label: '0 (0)' }
      ]}
    ],
    calculate: (values) => {
      const gcs = values.gcs || 4;
      const sbp = values.sbp || 4;
      const rr = values.rr || 4;
      const rts = (0.9368 * gcs + 0.7326 * sbp + 0.2908 * rr).toFixed(2);
      return { score: parseFloat(rts), max: 7.84 };
    },
    interpret: (score) => {
      if (score >= 7) return { level: 'success', text: `RTS ${score}`, detail: 'Survie > 95%' };
      if (score >= 5) return { level: 'warning', text: `RTS ${score}`, detail: 'Survie 60-90%' };
      if (score >= 3) return { level: 'danger', text: `RTS ${score}`, detail: 'Survie 25-60%' };
      return { level: 'danger', text: `RTS ${score}`, detail: 'Survie < 25%' };
    },
    formula: 'RTS = 0.9368×GCS + 0.7326×PAS + 0.2908×FR'
  },

  news2: {
    name: 'NEWS-2',
    category: 'Urgences',
    icon: Stethoscope,
    description: 'Détection détérioration clinique',
    color: 'orange',
    fields: [
      { id: 'resp', label: 'FR (/min)', type: 'select', options: [
        { value: 3, label: '≤8 (3)' }, { value: 1, label: '9-11 (1)' }, { value: 0, label: '12-20 (0)' },
        { value: 2, label: '21-24 (2)' }, { value: 3, label: '≥25 (3)' }
      ]},
      { id: 'spo2', label: 'SpO₂ (%)', type: 'select', options: [
        { value: 3, label: '≤91 (3)' }, { value: 2, label: '92-93 (2)' },
        { value: 1, label: '94-95 (1)' }, { value: 0, label: '≥96 (0)' }
      ]},
      { id: 'o2', label: 'Oxygénothérapie', type: 'select', options: [
        { value: 0, label: 'Air ambiant (0)' }, { value: 2, label: 'Sous O₂ (2)' }
      ]},
      { id: 'temp', label: 'Température (°C)', type: 'select', options: [
        { value: 3, label: '≤35.0 (3)' }, { value: 1, label: '35.1-36.0 (1)' },
        { value: 0, label: '36.1-38.0 (0)' }, { value: 1, label: '38.1-39.0 (1)' }, { value: 2, label: '≥39.1 (2)' }
      ]},
      { id: 'sbp', label: 'PAS (mmHg)', type: 'select', options: [
        { value: 3, label: '≤90 (3)' }, { value: 2, label: '91-100 (2)' }, { value: 1, label: '101-110 (1)' },
        { value: 0, label: '111-219 (0)' }, { value: 3, label: '≥220 (3)' }
      ]},
      { id: 'hr', label: 'FC (/min)', type: 'select', options: [
        { value: 3, label: '≤40 (3)' }, { value: 1, label: '41-50 (1)' }, { value: 0, label: '51-90 (0)' },
        { value: 1, label: '91-110 (1)' }, { value: 2, label: '111-130 (2)' }, { value: 3, label: '≥131 (3)' }
      ]},
      { id: 'avpu', label: 'Conscience', type: 'select', options: [
        { value: 0, label: 'Alerte (0)' }, { value: 3, label: 'Confusion/V/P/U (3)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 20 };
    },
    interpret: (score) => {
      if (score <= 4) return { level: 'success', text: `NEWS-2: ${score}`, detail: 'Risque faible. Surveillance 4-6h' };
      if (score <= 6) return { level: 'warning', text: `NEWS-2: ${score}`, detail: 'Risque modéré. Surveillance 1h, avis senior' };
      return { level: 'danger', text: `NEWS-2: ${score}`, detail: 'Risque élevé. Surveillance continue, avis réa urgent' };
    },
    formula: 'NEWS-2 = FR + SpO₂ + O₂ + T° + PAS + FC + Conscience'
  },

  canadianSyncope: {
    name: 'Canadian Syncope Risk Score',
    category: 'Urgences',
    icon: Stethoscope,
    description: 'Stratification risque syncope',
    color: 'orange',
    fields: [
      { id: 'cardiac', label: 'Prédisposition cardiaque', type: 'checkbox', points: 1 },
      { id: 'sbp', label: 'PAS < 90 ou > 180 mmHg', type: 'checkbox', points: 2 },
      { id: 'troponin', label: 'Troponine élevée', type: 'checkbox', points: 2 },
      { id: 'qrs', label: 'QRS anormal (BBG, BAV)', type: 'checkbox', points: 1 },
      { id: 'qt', label: 'QTc > 480 ms', type: 'checkbox', points: 2 },
      { id: 'vasovagal', label: 'Syncope vasovagale', type: 'checkbox', points: -2 },
      { id: 'ed', label: 'Évaluation aux urgences', type: 'select', options: [
        { value: 0, label: 'Normale (0)' },
        { value: 1, label: 'Anormale (1)' }
      ]}
    ],
    calculate: (values) => {
      let total = (values.ed || 0);
      if (values.cardiac) total += 1;
      if (values.sbp) total += 2;
      if (values.troponin) total += 2;
      if (values.qrs) total += 1;
      if (values.qt) total += 2;
      if (values.vasovagal) total -= 2;
      return { score: Math.max(total, -2), max: 11 };
    },
    interpret: (score) => {
      if (score <= 0) return { level: 'success', text: 'Risque très faible (0.4%)', detail: 'Retour à domicile possible' };
      if (score <= 3) return { level: 'warning', text: 'Risque faible (3%)', detail: 'Surveillance courte, holter ambulatoire' };
      if (score <= 5) return { level: 'warning', text: 'Risque modéré (8%)', detail: 'Hospitalisation à discuter' };
      return { level: 'danger', text: 'Risque élevé (>20%)', detail: 'Hospitalisation, bilan cardiologique urgent' };
    },
    formula: 'CSRS = Prédisposition + PA + Troponine + QRS + QT - Vasovagal + ED'
  },

  brest: {
    name: 'Score de Brest',
    category: 'Urgences',
    icon: Wind,
    description: 'OAP comme étiologie dyspnée',
    color: 'orange',
    fields: [
      { id: 'age75', label: 'Âge > 75 ans', type: 'checkbox', points: 2 },
      { id: 'orthopnea', label: 'Orthopnée', type: 'checkbox', points: 2 },
      { id: 'crackling', label: 'Crépitants bilatéraux', type: 'checkbox', points: 2 },
      { id: 'cardioHistory', label: 'ATCD insuffisance cardiaque', type: 'checkbox', points: 2 },
      { id: 'edema', label: 'Œdèmes des MI', type: 'checkbox', points: 1 },
      { id: 'noCough', label: 'Pas de fièvre ni toux', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.age75) total += 2;
      if (values.orthopnea) total += 2;
      if (values.crackling) total += 2;
      if (values.cardioHistory) total += 2;
      if (values.edema) total += 1;
      if (values.noCough) total += 1;
      return { score: total, max: 10 };
    },
    interpret: (score) => {
      if (score <= 4) return { level: 'success', text: `Brest ${score}`, detail: 'OAP peu probable. Rechercher autre cause' };
      if (score <= 6) return { level: 'warning', text: `Brest ${score}`, detail: 'OAP possible. BNP/NT-proBNP, ETT' };
      return { level: 'danger', text: `Brest ${score}`, detail: 'OAP probable. Traitement diurétique, VNI si détresse' };
    },
    formula: 'Brest = Âge>75 + Orthopnée + Crépitants + ATCD IC + OMI + Pas fièvre/toux'
  }
};

// ==================== NEUROLOGIE ÉTENDUE ====================
export const NEUROLOGY_EXTENDED_SCORES = {
  nihss: {
    name: 'NIHSS',
    category: 'Neurologie',
    icon: Brain,
    description: 'Gravité AVC ischémique',
    color: 'purple',
    fields: [
      { id: 'consciousness', label: '1a. Conscience', type: 'select', options: [
        { value: 0, label: '0 - Vigilant' }, { value: 1, label: '1 - Non vigilant, réactif' },
        { value: 2, label: '2 - Non vigilant, peu réactif' }, { value: 3, label: '3 - Coma' }
      ]},
      { id: 'questions', label: '1b. Questions (mois, âge)', type: 'select', options: [
        { value: 0, label: '0 - Répond aux 2' }, { value: 1, label: '1 - Répond à 1' }, { value: 2, label: '2 - Répond à 0' }
      ]},
      { id: 'commands', label: '1c. Ordres (ouvrir/fermer yeux, serrer main)', type: 'select', options: [
        { value: 0, label: '0 - Exécute les 2' }, { value: 1, label: '1 - Exécute 1' }, { value: 2, label: '2 - Exécute 0' }
      ]},
      { id: 'gaze', label: '2. Oculomotricité', type: 'select', options: [
        { value: 0, label: '0 - Normal' }, { value: 1, label: '1 - Paralysie partielle' }, { value: 2, label: '2 - Déviation forcée' }
      ]},
      { id: 'visual', label: '3. Champ visuel', type: 'select', options: [
        { value: 0, label: '0 - Normal' }, { value: 1, label: '1 - HLH partielle' },
        { value: 2, label: '2 - HLH complète' }, { value: 3, label: '3 - Cécité bilatérale' }
      ]},
      { id: 'facial', label: '4. Paralysie faciale', type: 'select', options: [
        { value: 0, label: '0 - Absente' }, { value: 1, label: '1 - Mineure' },
        { value: 2, label: '2 - Partielle' }, { value: 3, label: '3 - Complète' }
      ]},
      { id: 'armL', label: '5a. Motricité MS gauche', type: 'select', options: [
        { value: 0, label: '0 - Pas de chute' }, { value: 1, label: '1 - Chute' },
        { value: 2, label: '2 - Effort contre pesanteur' }, { value: 3, label: '3 - Pas contre pesanteur' }, { value: 4, label: '4 - Plégie' }
      ]},
      { id: 'armR', label: '5b. Motricité MS droit', type: 'select', options: [
        { value: 0, label: '0 - Pas de chute' }, { value: 1, label: '1 - Chute' },
        { value: 2, label: '2 - Effort contre pesanteur' }, { value: 3, label: '3 - Pas contre pesanteur' }, { value: 4, label: '4 - Plégie' }
      ]},
      { id: 'legL', label: '6a. Motricité MI gauche', type: 'select', options: [
        { value: 0, label: '0 - Pas de chute' }, { value: 1, label: '1 - Chute' },
        { value: 2, label: '2 - Effort contre pesanteur' }, { value: 3, label: '3 - Pas contre pesanteur' }, { value: 4, label: '4 - Plégie' }
      ]},
      { id: 'legR', label: '6b. Motricité MI droit', type: 'select', options: [
        { value: 0, label: '0 - Pas de chute' }, { value: 1, label: '1 - Chute' },
        { value: 2, label: '2 - Effort contre pesanteur' }, { value: 3, label: '3 - Pas contre pesanteur' }, { value: 4, label: '4 - Plégie' }
      ]},
      { id: 'ataxia', label: '7. Ataxie', type: 'select', options: [
        { value: 0, label: '0 - Absente' }, { value: 1, label: '1 - 1 membre' }, { value: 2, label: '2 - 2 membres' }
      ]},
      { id: 'sensory', label: '8. Sensibilité', type: 'select', options: [
        { value: 0, label: '0 - Normale' }, { value: 1, label: '1 - Hypoesthésie légère' }, { value: 2, label: '2 - Hypoesthésie sévère' }
      ]},
      { id: 'language', label: '9. Langage', type: 'select', options: [
        { value: 0, label: '0 - Normal' }, { value: 1, label: '1 - Aphasie légère' },
        { value: 2, label: '2 - Aphasie sévère' }, { value: 3, label: '3 - Mutisme' }
      ]},
      { id: 'dysarthria', label: '10. Dysarthrie', type: 'select', options: [
        { value: 0, label: '0 - Normale' }, { value: 1, label: '1 - Légère' }, { value: 2, label: '2 - Sévère' }
      ]},
      { id: 'neglect', label: '11. Extinction/Négligence', type: 'select', options: [
        { value: 0, label: '0 - Absente' }, { value: 1, label: '1 - Partielle' }, { value: 2, label: '2 - Complète' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 42 };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: 'Pas de déficit', detail: 'AIT ou résolution' };
      if (score <= 4) return { level: 'success', text: 'AVC mineur', detail: 'Thrombolyse à discuter' };
      if (score <= 15) return { level: 'warning', text: 'AVC modéré', detail: 'Thrombolyse < 4h30' };
      if (score <= 25) return { level: 'danger', text: 'AVC sévère', detail: 'Thrombectomie à discuter' };
      return { level: 'danger', text: 'AVC très sévère', detail: 'Pronostic réservé' };
    },
    formula: 'NIHSS = Somme des 15 items (0-42)'
  },

  abcd2: {
    name: 'Score ABCD²',
    category: 'Neurologie',
    icon: Brain,
    description: 'Risque AVC après AIT',
    color: 'purple',
    fields: [
      { id: 'age', label: 'Âge ≥ 60 ans', type: 'checkbox', points: 1 },
      { id: 'bp', label: 'PA ≥ 140/90 mmHg', type: 'checkbox', points: 1 },
      { id: 'clinical', label: 'Clinique', type: 'select', options: [
        { value: 0, label: '0 - Autre' },
        { value: 1, label: '1 - Trouble parole sans faiblesse' },
        { value: 2, label: '2 - Faiblesse unilatérale' }
      ]},
      { id: 'duration', label: 'Durée', type: 'select', options: [
        { value: 0, label: '0 - < 10 min' },
        { value: 1, label: '1 - 10-59 min' },
        { value: 2, label: '2 - ≥ 60 min' }
      ]},
      { id: 'diabetes', label: 'Diabète', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = (values.clinical || 0) + (values.duration || 0);
      if (values.age) total += 1;
      if (values.bp) total += 1;
      if (values.diabetes) total += 1;
      return { score: total, max: 7 };
    },
    interpret: (score) => {
      if (score <= 3) return { level: 'success', text: `ABCD² ${score}`, detail: 'Risque faible (1-2% à 2j). Bilan ambulatoire rapide' };
      if (score <= 5) return { level: 'warning', text: `ABCD² ${score}`, detail: 'Risque modéré (4-5% à 2j). Hospitalisation recommandée' };
      return { level: 'danger', text: `ABCD² ${score}`, detail: 'Risque élevé (8% à 2j). Hospitalisation urgente, IRM' };
    },
    formula: 'ABCD² = Age + BP + Clinical + Duration + Diabetes'
  },

  ich: {
    name: 'Score ICH',
    category: 'Neurologie',
    icon: Brain,
    description: 'Mortalité hémorragie intracérébrale',
    color: 'purple',
    fields: [
      { id: 'gcs', label: 'Glasgow', type: 'select', options: [
        { value: 2, label: '3-4 (2 pts)' }, { value: 1, label: '5-12 (1 pt)' }, { value: 0, label: '13-15 (0 pt)' }
      ]},
      { id: 'volume', label: 'Volume hématome', type: 'select', options: [
        { value: 0, label: '< 30 mL (0 pt)' }, { value: 1, label: '≥ 30 mL (1 pt)' }
      ]},
      { id: 'ivh', label: 'Hémorragie intraventriculaire', type: 'checkbox', points: 1 },
      { id: 'infratentorial', label: 'Origine infratentorielle', type: 'checkbox', points: 1 },
      { id: 'age80', label: 'Âge ≥ 80 ans', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = (values.gcs || 0) + (values.volume || 0);
      if (values.ivh) total += 1;
      if (values.infratentorial) total += 1;
      if (values.age80) total += 1;
      return { score: total, max: 6 };
    },
    interpret: (score) => {
      const mortalities = ['0%', '13%', '26%', '72%', '97%', '100%', '100%'];
      return { 
        level: score <= 1 ? 'success' : score <= 3 ? 'warning' : 'danger', 
        text: `ICH ${score}`, 
        detail: `Mortalité à 30j: ${mortalities[score]}` 
      };
    },
    formula: 'ICH = GCS + Volume + HIV + Infratentoriel + Âge≥80'
  },

  four: {
    name: 'Score FOUR',
    category: 'Neurologie',
    icon: Brain,
    description: 'Évaluation conscience (remplace Glasgow en réa)',
    color: 'purple',
    fields: [
      { id: 'eyes', label: 'Réponse oculaire', type: 'select', options: [
        { value: 4, label: '4 - Yeux ouverts, poursuite ou clignement à la demande' },
        { value: 3, label: '3 - Yeux ouverts mais pas de poursuite' },
        { value: 2, label: '2 - Yeux fermés, ouvre à la voix forte' },
        { value: 1, label: '1 - Yeux fermés, ouvre à la douleur' },
        { value: 0, label: '0 - Yeux fermés même à la douleur' }
      ]},
      { id: 'motor', label: 'Réponse motrice', type: 'select', options: [
        { value: 4, label: '4 - Pouces levés, poing fermé ou signe de paix' },
        { value: 3, label: '3 - Localise la douleur' },
        { value: 2, label: '2 - Réponse en flexion' },
        { value: 1, label: '1 - Réponse en extension' },
        { value: 0, label: '0 - Pas de réponse ou état de mal myoclonique' }
      ]},
      { id: 'brainstem', label: 'Réflexes du tronc', type: 'select', options: [
        { value: 4, label: '4 - Pupilles et cornéens présents' },
        { value: 3, label: '3 - Une pupille dilatée fixe' },
        { value: 2, label: '2 - Pupilles ou cornéens absents' },
        { value: 1, label: '1 - Pupilles et cornéens absents' },
        { value: 0, label: '0 - Pupilles, cornéens et toux absents' }
      ]},
      { id: 'respiration', label: 'Respiration', type: 'select', options: [
        { value: 4, label: '4 - Non intubé, respiration régulière' },
        { value: 3, label: '3 - Non intubé, Cheyne-Stokes' },
        { value: 2, label: '2 - Non intubé, respiration irrégulière' },
        { value: 1, label: '1 - Intubé, déclenche le respirateur' },
        { value: 0, label: '0 - Intubé, ne déclenche pas ou apnée' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 16 };
    },
    interpret: (score) => {
      if (score >= 12) return { level: 'success', text: `FOUR ${score}`, detail: 'Conscience préservée ou altération légère' };
      if (score >= 8) return { level: 'warning', text: `FOUR ${score}`, detail: 'Altération modérée de la conscience' };
      if (score >= 4) return { level: 'danger', text: `FOUR ${score}`, detail: 'Coma' };
      return { level: 'danger', text: `FOUR ${score}`, detail: 'Coma profond ou mort encéphalique possible' };
    },
    formula: 'FOUR = Eye + Motor + Brainstem + Respiration (0-16)'
  },

  rankinModified: {
    name: 'Échelle de Rankin modifiée',
    category: 'Neurologie',
    icon: Brain,
    description: 'Handicap global post-AVC',
    color: 'purple',
    fields: [
      { id: 'score', label: 'Score mRS', type: 'select', options: [
        { value: 0, label: '0 - Aucun symptôme' },
        { value: 1, label: '1 - Pas de handicap significatif malgré symptômes' },
        { value: 2, label: '2 - Handicap léger, autonome' },
        { value: 3, label: '3 - Handicap modéré, marche seul, aide quotidienne' },
        { value: 4, label: '4 - Handicap modérément sévère, ne marche pas seul' },
        { value: 5, label: '5 - Handicap sévère, grabataire, soins constants' },
        { value: 6, label: '6 - Décès' }
      ]}
    ],
    calculate: (values) => ({ score: values.score || 0, max: 6 }),
    interpret: (score) => {
      if (score <= 1) return { level: 'success', text: `mRS ${score}`, detail: 'Excellent pronostic fonctionnel' };
      if (score <= 2) return { level: 'success', text: `mRS ${score}`, detail: 'Bon pronostic, vie indépendante' };
      if (score <= 3) return { level: 'warning', text: `mRS ${score}`, detail: 'Pronostic modéré, aide nécessaire' };
      return { level: 'danger', text: `mRS ${score}`, detail: 'Pronostic défavorable, dépendance importante' };
    },
    formula: 'mRS 0-6: évaluation du handicap fonctionnel'
  },

  barthel: {
    name: 'Index de Barthel',
    category: 'Neurologie',
    icon: Brain,
    description: 'Évaluation autonomie ADL',
    color: 'purple',
    fields: [
      { id: 'feeding', label: 'Alimentation', type: 'select', options: [
        { value: 0, label: '0 - Incapable' }, { value: 5, label: '5 - Aide' }, { value: 10, label: '10 - Indépendant' }
      ]},
      { id: 'bathing', label: 'Toilette', type: 'select', options: [
        { value: 0, label: '0 - Dépendant' }, { value: 5, label: '5 - Indépendant' }
      ]},
      { id: 'grooming', label: 'Soins personnels', type: 'select', options: [
        { value: 0, label: '0 - Aide nécessaire' }, { value: 5, label: '5 - Indépendant' }
      ]},
      { id: 'dressing', label: 'Habillage', type: 'select', options: [
        { value: 0, label: '0 - Dépendant' }, { value: 5, label: '5 - Aide' }, { value: 10, label: '10 - Indépendant' }
      ]},
      { id: 'bowels', label: 'Contrôle intestinal', type: 'select', options: [
        { value: 0, label: '0 - Incontinent' }, { value: 5, label: '5 - Accidents occasionnels' }, { value: 10, label: '10 - Continent' }
      ]},
      { id: 'bladder', label: 'Contrôle vésical', type: 'select', options: [
        { value: 0, label: '0 - Incontinent' }, { value: 5, label: '5 - Accidents occasionnels' }, { value: 10, label: '10 - Continent' }
      ]},
      { id: 'toilet', label: 'Utilisation toilettes', type: 'select', options: [
        { value: 0, label: '0 - Dépendant' }, { value: 5, label: '5 - Aide' }, { value: 10, label: '10 - Indépendant' }
      ]},
      { id: 'transfer', label: 'Transfert lit-chaise', type: 'select', options: [
        { value: 0, label: '0 - Incapable' }, { value: 5, label: '5 - Aide importante' },
        { value: 10, label: '10 - Aide mineure' }, { value: 15, label: '15 - Indépendant' }
      ]},
      { id: 'mobility', label: 'Mobilité', type: 'select', options: [
        { value: 0, label: '0 - Immobile' }, { value: 5, label: '5 - Fauteuil roulant' },
        { value: 10, label: '10 - Marche avec aide' }, { value: 15, label: '15 - Indépendant' }
      ]},
      { id: 'stairs', label: 'Escaliers', type: 'select', options: [
        { value: 0, label: '0 - Incapable' }, { value: 5, label: '5 - Aide' }, { value: 10, label: '10 - Indépendant' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 100 };
    },
    interpret: (score) => {
      if (score >= 80) return { level: 'success', text: `Barthel ${score}/100`, detail: 'Indépendance bonne à totale' };
      if (score >= 60) return { level: 'warning', text: `Barthel ${score}/100`, detail: 'Dépendance légère' };
      if (score >= 40) return { level: 'warning', text: `Barthel ${score}/100`, detail: 'Dépendance modérée' };
      if (score >= 20) return { level: 'danger', text: `Barthel ${score}/100`, detail: 'Dépendance sévère' };
      return { level: 'danger', text: `Barthel ${score}/100`, detail: 'Dépendance totale' };
    },
    formula: 'Barthel = Somme de 10 activités de la vie quotidienne'
  }
};

// ==================== PNEUMOLOGIE ÉTENDUE ====================
export const PULMONOLOGY_EXTENDED_SCORES = {
  geneveRevised: {
    name: 'Genève révisé',
    category: 'Pneumologie',
    icon: Wind,
    description: 'Probabilité embolie pulmonaire',
    color: 'cyan',
    fields: [
      { id: 'age65', label: 'Âge > 65 ans', type: 'checkbox', points: 1 },
      { id: 'dvtHistory', label: 'ATCD TVP ou EP', type: 'checkbox', points: 3 },
      { id: 'surgery', label: 'Chirurgie ou fracture < 1 mois', type: 'checkbox', points: 2 },
      { id: 'cancer', label: 'Cancer actif', type: 'checkbox', points: 2 },
      { id: 'uniPain', label: 'Douleur unilatérale MI', type: 'checkbox', points: 3 },
      { id: 'hemoptysis', label: 'Hémoptysie', type: 'checkbox', points: 2 },
      { id: 'hr75', label: 'FC 75-94/min', type: 'checkbox', points: 3 },
      { id: 'hr95', label: 'FC ≥ 95/min', type: 'checkbox', points: 5 },
      { id: 'edema', label: 'Douleur palpation + œdème MI', type: 'checkbox', points: 4 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.age65) total += 1;
      if (values.dvtHistory) total += 3;
      if (values.surgery) total += 2;
      if (values.cancer) total += 2;
      if (values.uniPain) total += 3;
      if (values.hemoptysis) total += 2;
      // FC: only count the highest
      if (values.hr95) total += 5;
      else if (values.hr75) total += 3;
      if (values.edema) total += 4;
      return { score: total, max: 22 };
    },
    interpret: (score) => {
      if (score <= 3) return { level: 'success', text: 'Probabilité faible (8%)', detail: 'D-Dimères, si négatifs: EP exclue' };
      if (score <= 10) return { level: 'warning', text: 'Probabilité intermédiaire (28%)', detail: 'D-Dimères puis angio-TDM si positifs' };
      return { level: 'danger', text: 'Probabilité forte (74%)', detail: 'Angio-TDM directement' };
    },
    formula: 'Genève révisé: somme des critères'
  },

  perc: {
    name: 'Règle PERC',
    category: 'Pneumologie',
    icon: Wind,
    description: 'Exclusion EP sans D-Dimères',
    color: 'cyan',
    fields: [
      { id: 'age50', label: 'Âge ≥ 50 ans', type: 'checkbox', points: 1 },
      { id: 'hr100', label: 'FC ≥ 100/min', type: 'checkbox', points: 1 },
      { id: 'spo2', label: 'SpO₂ < 95%', type: 'checkbox', points: 1 },
      { id: 'hemoptysis', label: 'Hémoptysie', type: 'checkbox', points: 1 },
      { id: 'estrogen', label: 'Œstrogènes', type: 'checkbox', points: 1 },
      { id: 'history', label: 'ATCD TVP/EP', type: 'checkbox', points: 1 },
      { id: 'surgery', label: 'Chirurgie/trauma < 4 sem', type: 'checkbox', points: 1 },
      { id: 'edema', label: 'Œdème unilatéral MI', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 8 };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: 'PERC négatif', detail: 'EP exclue si probabilité pré-test faible. Pas de D-Dimères' };
      return { level: 'warning', text: 'PERC positif', detail: 'Poursuivre bilan: D-Dimères ou angio-TDM' };
    },
    formula: 'PERC négatif si 8 critères tous absents'
  },

  pesi: {
    name: 'Score PESI',
    category: 'Pneumologie',
    icon: Wind,
    description: 'Gravité embolie pulmonaire',
    color: 'cyan',
    fields: [
      { id: 'age', label: 'Âge (années)', type: 'number', min: 18, max: 120 },
      { id: 'male', label: 'Sexe masculin', type: 'checkbox', points: 10 },
      { id: 'cancer', label: 'Cancer', type: 'checkbox', points: 30 },
      { id: 'chf', label: 'Insuffisance cardiaque', type: 'checkbox', points: 10 },
      { id: 'copd', label: 'BPCO', type: 'checkbox', points: 10 },
      { id: 'hr110', label: 'FC ≥ 110/min', type: 'checkbox', points: 20 },
      { id: 'sbp100', label: 'PAS < 100 mmHg', type: 'checkbox', points: 30 },
      { id: 'rr30', label: 'FR ≥ 30/min', type: 'checkbox', points: 20 },
      { id: 'temp36', label: 'T° < 36°C', type: 'checkbox', points: 20 },
      { id: 'altered', label: 'Altération conscience', type: 'checkbox', points: 60 },
      { id: 'spo290', label: 'SpO₂ < 90%', type: 'checkbox', points: 20 }
    ],
    calculate: (values) => {
      let total = values.age || 0;
      if (values.male) total += 10;
      if (values.cancer) total += 30;
      if (values.chf) total += 10;
      if (values.copd) total += 10;
      if (values.hr110) total += 20;
      if (values.sbp100) total += 30;
      if (values.rr30) total += 20;
      if (values.temp36) total += 20;
      if (values.altered) total += 60;
      if (values.spo290) total += 20;
      return { score: total, max: 'variable' };
    },
    interpret: (score) => {
      if (score <= 65) return { level: 'success', text: 'PESI I - Très faible', detail: 'Mortalité 0-1.6%. Traitement ambulatoire possible' };
      if (score <= 85) return { level: 'success', text: 'PESI II - Faible', detail: 'Mortalité 1.7-3.5%. Hospitalisation courte' };
      if (score <= 105) return { level: 'warning', text: 'PESI III - Intermédiaire', detail: 'Mortalité 3.2-7.1%. Hospitalisation' };
      if (score <= 125) return { level: 'danger', text: 'PESI IV - Élevé', detail: 'Mortalité 4-11.4%. Hospitalisation' };
      return { level: 'danger', text: 'PESI V - Très élevé', detail: 'Mortalité 10-24.5%. USI' };
    },
    formula: 'PESI = Âge + Critères cliniques'
  },

  fine: {
    name: 'Score de Fine (PSI)',
    category: 'Pneumologie',
    icon: Wind,
    description: 'Gravité pneumopathie',
    color: 'cyan',
    fields: [
      { id: 'age', label: 'Âge (années)', type: 'number', min: 18, max: 120 },
      { id: 'female', label: 'Sexe féminin (-10)', type: 'checkbox', points: -10 },
      { id: 'nursing', label: 'Institution', type: 'checkbox', points: 10 },
      { id: 'cancer', label: 'Néoplasie', type: 'checkbox', points: 30 },
      { id: 'liver', label: 'Hépatopathie', type: 'checkbox', points: 20 },
      { id: 'chf', label: 'Insuffisance cardiaque', type: 'checkbox', points: 10 },
      { id: 'stroke', label: 'Maladie cérébrovasculaire', type: 'checkbox', points: 10 },
      { id: 'renal', label: 'Néphropathie', type: 'checkbox', points: 10 },
      { id: 'confusion', label: 'Confusion', type: 'checkbox', points: 20 },
      { id: 'tachypnea', label: 'FR ≥ 30/min', type: 'checkbox', points: 20 },
      { id: 'hypotension', label: 'PAS < 90 mmHg', type: 'checkbox', points: 20 },
      { id: 'temp', label: 'T° < 35 ou ≥ 40°C', type: 'checkbox', points: 15 },
      { id: 'tachycardia', label: 'FC ≥ 125/min', type: 'checkbox', points: 10 }
    ],
    calculate: (values) => {
      let total = values.age || 0;
      if (values.female) total -= 10;
      if (values.nursing) total += 10;
      if (values.cancer) total += 30;
      if (values.liver) total += 20;
      if (values.chf) total += 10;
      if (values.stroke) total += 10;
      if (values.renal) total += 10;
      if (values.confusion) total += 20;
      if (values.tachypnea) total += 20;
      if (values.hypotension) total += 20;
      if (values.temp) total += 15;
      if (values.tachycardia) total += 10;
      return { score: total, max: 'variable' };
    },
    interpret: (score) => {
      if (score <= 50) return { level: 'success', text: 'Classe I', detail: 'Mortalité 0.1%. Ambulatoire' };
      if (score <= 70) return { level: 'success', text: 'Classe II', detail: 'Mortalité 0.6%. Ambulatoire' };
      if (score <= 90) return { level: 'warning', text: 'Classe III', detail: 'Mortalité 2.8%. Hospitalisation courte' };
      if (score <= 130) return { level: 'danger', text: 'Classe IV', detail: 'Mortalité 8.2%. Hospitalisation' };
      return { level: 'danger', text: 'Classe V', detail: 'Mortalité 29.2%. USI' };
    },
    formula: 'PSI = Âge + Comorbidités + Clinique'
  },

  curb65: {
    name: 'CRB-65 / CURB-65',
    category: 'Pneumologie',
    icon: Wind,
    description: 'Gravité pneumopathie (simplifié)',
    color: 'cyan',
    fields: [
      { id: 'confusion', label: 'Confusion', type: 'checkbox', points: 1 },
      { id: 'urea', label: 'Urée > 7 mmol/L (CURB)', type: 'checkbox', points: 1 },
      { id: 'rr', label: 'FR ≥ 30/min', type: 'checkbox', points: 1 },
      { id: 'bp', label: 'PA syst < 90 ou diast ≤ 60', type: 'checkbox', points: 1 },
      { id: 'age', label: 'Âge ≥ 65 ans', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 5 };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: `CRB-65: ${score}`, detail: 'Mortalité < 1%. Ambulatoire' };
      if (score <= 1) return { level: 'success', text: `CRB-65: ${score}`, detail: 'Mortalité 1.5%. Ambulatoire ou hospit courte' };
      if (score === 2) return { level: 'warning', text: `CRB-65: ${score}`, detail: 'Mortalité 9%. Hospitalisation' };
      return { level: 'danger', text: `CRB-65: ${score}`, detail: 'Mortalité 22%. USI à discuter' };
    },
    formula: 'CRB-65: Confusion + (Urée) + Resp ≥30 + BP + ≥65 ans'
  },

  pao2fio2: {
    name: 'Rapport PaO₂/FiO₂',
    category: 'Pneumologie',
    icon: Wind,
    description: 'Sévérité hypoxémie / SDRA',
    color: 'cyan',
    fields: [
      { id: 'pao2', label: 'PaO₂ (mmHg)', type: 'number', min: 20, max: 600 },
      { id: 'fio2', label: 'FiO₂ (%)', type: 'number', min: 21, max: 100 }
    ],
    calculate: (values) => {
      const pao2 = values.pao2 || 80;
      const fio2 = (values.fio2 || 21) / 100;
      const ratio = Math.round(pao2 / fio2);
      return { score: ratio, max: '' };
    },
    interpret: (score) => {
      if (score >= 400) return { level: 'success', text: `P/F: ${score}`, detail: 'Normal' };
      if (score >= 300) return { level: 'warning', text: `P/F: ${score}`, detail: 'Hypoxémie légère / SDRA léger' };
      if (score >= 200) return { level: 'danger', text: `P/F: ${score}`, detail: 'SDRA modéré. VNI ou IOT' };
      if (score >= 100) return { level: 'danger', text: `P/F: ${score}`, detail: 'SDRA sévère. Ventilation protectrice, DV' };
      return { level: 'danger', text: `P/F: ${score}`, detail: 'SDRA très sévère. ECMO à discuter' };
    },
    formula: 'P/F = PaO₂ / FiO₂ (normal > 400)'
  },

  roxIndex: {
    name: 'Index ROX',
    category: 'Pneumologie',
    icon: Wind,
    description: 'Échec oxygénothérapie haut débit',
    color: 'cyan',
    fields: [
      { id: 'spo2', label: 'SpO₂ (%)', type: 'number', min: 50, max: 100 },
      { id: 'fio2', label: 'FiO₂ (%)', type: 'number', min: 21, max: 100 },
      { id: 'rr', label: 'FR (/min)', type: 'number', min: 5, max: 60 }
    ],
    calculate: (values) => {
      const spo2 = values.spo2 || 95;
      const fio2 = values.fio2 || 60;
      const rr = values.rr || 25;
      const rox = ((spo2 / fio2) / rr * 100).toFixed(2);
      return { score: parseFloat(rox), max: '' };
    },
    interpret: (score) => {
      if (score >= 4.88) return { level: 'success', text: `ROX: ${score}`, detail: 'Faible risque d\'échec OHD' };
      if (score >= 3.85) return { level: 'warning', text: `ROX: ${score}`, detail: 'Surveillance rapprochée' };
      return { level: 'danger', text: `ROX: ${score}`, detail: 'Risque élevé d\'échec. Préparer intubation' };
    },
    formula: 'ROX = (SpO₂/FiO₂) / FR × 100'
  }
};

// ==================== RÉANIMATION ÉTENDUE ====================
export const ICU_EXTENDED_SCORES = {
  apacheII: {
    name: 'APACHE II',
    category: 'Réanimation',
    icon: Activity,
    description: 'Gravité patient en réanimation',
    color: 'orange',
    fields: [
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 0, label: '< 45 ans (0)' }, { value: 2, label: '45-54 (2)' },
        { value: 3, label: '55-64 (3)' }, { value: 5, label: '65-74 (5)' }, { value: 6, label: '≥ 75 (6)' }
      ]},
      { id: 'chronicHealth', label: 'Maladie chronique', type: 'select', options: [
        { value: 0, label: 'Aucune (0)' },
        { value: 2, label: 'Non opéré ou urgence opératoire (2)' },
        { value: 5, label: 'Chirurgie programmée (5)' }
      ]},
      { id: 'temp', label: 'Température (°C)', type: 'select', options: [
        { value: 4, label: '≥ 41 (4)' }, { value: 3, label: '39-40.9 (3)' }, { value: 1, label: '38.5-38.9 (1)' },
        { value: 0, label: '36-38.4 (0)' }, { value: 1, label: '34-35.9 (1)' }, { value: 2, label: '32-33.9 (2)' },
        { value: 3, label: '30-31.9 (3)' }, { value: 4, label: '< 30 (4)' }
      ]},
      { id: 'map', label: 'PAM (mmHg)', type: 'select', options: [
        { value: 4, label: '≥ 160 (4)' }, { value: 3, label: '130-159 (3)' }, { value: 2, label: '110-129 (2)' },
        { value: 0, label: '70-109 (0)' }, { value: 2, label: '50-69 (2)' }, { value: 4, label: '< 50 (4)' }
      ]},
      { id: 'hr', label: 'FC (/min)', type: 'select', options: [
        { value: 4, label: '≥ 180 (4)' }, { value: 3, label: '140-179 (3)' }, { value: 2, label: '110-139 (2)' },
        { value: 0, label: '70-109 (0)' }, { value: 2, label: '55-69 (2)' }, { value: 3, label: '40-54 (3)' }, { value: 4, label: '< 40 (4)' }
      ]},
      { id: 'rr', label: 'FR (/min)', type: 'select', options: [
        { value: 4, label: '≥ 50 (4)' }, { value: 3, label: '35-49 (3)' }, { value: 1, label: '25-34 (1)' },
        { value: 0, label: '12-24 (0)' }, { value: 1, label: '10-11 (1)' }, { value: 2, label: '6-9 (2)' }, { value: 4, label: '< 6 (4)' }
      ]},
      { id: 'gcs', label: 'Glasgow (15 - GCS)', type: 'number', min: 0, max: 12 },
      { id: 'creatinine', label: 'Créatinine (points)', type: 'select', options: [
        { value: 0, label: '< 133 µmol/L (0)' }, { value: 2, label: '133-176 (2)' },
        { value: 3, label: '177-309 (3)' }, { value: 4, label: '≥ 310 (4)' }
      ]},
      { id: 'hematocrit', label: 'Hématocrite', type: 'select', options: [
        { value: 4, label: '≥ 60% (4)' }, { value: 2, label: '50-59.9% (2)' }, { value: 0, label: '30-49% (0)' },
        { value: 2, label: '20-29.9% (2)' }, { value: 4, label: '< 20% (4)' }
      ]}
    ],
    calculate: (values) => {
      let total = 0;
      Object.keys(values).forEach(k => {
        if (k !== 'gcs') total += (values[k] || 0);
      });
      total += (values.gcs || 0); // GCS score is already 15-GCS
      return { score: total, max: 71 };
    },
    interpret: (score) => {
      if (score <= 4) return { level: 'success', text: `APACHE II: ${score}`, detail: 'Mortalité ~4%' };
      if (score <= 9) return { level: 'success', text: `APACHE II: ${score}`, detail: 'Mortalité ~8%' };
      if (score <= 14) return { level: 'warning', text: `APACHE II: ${score}`, detail: 'Mortalité ~15%' };
      if (score <= 24) return { level: 'warning', text: `APACHE II: ${score}`, detail: 'Mortalité ~25%' };
      if (score <= 34) return { level: 'danger', text: `APACHE II: ${score}`, detail: 'Mortalité ~55%' };
      return { level: 'danger', text: `APACHE II: ${score}`, detail: 'Mortalité ~85%' };
    },
    formula: 'APACHE II = Physiologie aiguë + Âge + Maladie chronique'
  },

  igsII: {
    name: 'IGS II (SAPS II)',
    category: 'Réanimation',
    icon: Activity,
    description: 'Gravité patient en réanimation',
    color: 'orange',
    fields: [
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 0, label: '< 40 (0)' }, { value: 7, label: '40-59 (7)' },
        { value: 12, label: '60-69 (12)' }, { value: 15, label: '70-74 (15)' },
        { value: 16, label: '75-79 (16)' }, { value: 18, label: '≥ 80 (18)' }
      ]},
      { id: 'admission', label: 'Type admission', type: 'select', options: [
        { value: 0, label: 'Chirurgie programmée (0)' },
        { value: 6, label: 'Médical (6)' },
        { value: 8, label: 'Chirurgie urgente (8)' }
      ]},
      { id: 'chronicDisease', label: 'Maladie chronique', type: 'select', options: [
        { value: 0, label: 'Aucune (0)' },
        { value: 9, label: 'Hémato/SIDA/Cancer méta (9)' },
        { value: 10, label: 'Cancer méta (10)' },
        { value: 17, label: 'SIDA (17)' }
      ]},
      { id: 'gcs', label: 'Glasgow', type: 'select', options: [
        { value: 26, label: '3-5 (26)' }, { value: 13, label: '6-8 (13)' },
        { value: 7, label: '9-10 (7)' }, { value: 5, label: '11-13 (5)' }, { value: 0, label: '14-15 (0)' }
      ]},
      { id: 'sbp', label: 'PAS (mmHg)', type: 'select', options: [
        { value: 13, label: '< 70 (13)' }, { value: 5, label: '70-99 (5)' },
        { value: 0, label: '100-199 (0)' }, { value: 2, label: '≥ 200 (2)' }
      ]},
      { id: 'hr', label: 'FC (/min)', type: 'select', options: [
        { value: 11, label: '< 40 (11)' }, { value: 2, label: '40-69 (2)' },
        { value: 0, label: '70-119 (0)' }, { value: 4, label: '120-159 (4)' }, { value: 7, label: '≥ 160 (7)' }
      ]},
      { id: 'temp', label: 'Température (°C)', type: 'select', options: [
        { value: 0, label: '< 39 (0)' }, { value: 3, label: '≥ 39 (3)' }
      ]},
      { id: 'pao2fio2', label: 'PaO₂/FiO₂ (si VM ou CPAP)', type: 'select', options: [
        { value: 0, label: 'Non ventilé (0)' },
        { value: 6, label: '< 100 (6)' }, { value: 6, label: '100-199 (6)' }, { value: 0, label: '≥ 200 (0)' }
      ]},
      { id: 'urea', label: 'Urée (mmol/L)', type: 'select', options: [
        { value: 0, label: '< 10 (0)' }, { value: 6, label: '10-29.9 (6)' }, { value: 10, label: '≥ 30 (10)' }
      ]},
      { id: 'diuresis', label: 'Diurèse (L/24h)', type: 'select', options: [
        { value: 11, label: '< 0.5 (11)' }, { value: 4, label: '0.5-0.99 (4)' }, { value: 0, label: '≥ 1 (0)' }
      ]},
      { id: 'sodium', label: 'Natrémie (mmol/L)', type: 'select', options: [
        { value: 5, label: '< 125 (5)' }, { value: 0, label: '125-144 (0)' }, { value: 1, label: '≥ 145 (1)' }
      ]},
      { id: 'potassium', label: 'Kaliémie (mmol/L)', type: 'select', options: [
        { value: 3, label: '< 3 (3)' }, { value: 0, label: '3-4.9 (0)' }, { value: 3, label: '≥ 5 (3)' }
      ]},
      { id: 'bilirubin', label: 'Bilirubine (µmol/L)', type: 'select', options: [
        { value: 0, label: '< 68 (0)' }, { value: 4, label: '68-102 (4)' }, { value: 9, label: '> 102 (9)' }
      ]},
      { id: 'wbc', label: 'Leucocytes (×10⁹/L)', type: 'select', options: [
        { value: 12, label: '< 1 (12)' }, { value: 0, label: '1-19.9 (0)' }, { value: 3, label: '≥ 20 (3)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 163 };
    },
    interpret: (score) => {
      // Logistic regression for mortality
      const logit = -7.7631 + 0.0737 * score + 0.9971 * Math.log(score + 1);
      const mortality = Math.round(100 * Math.exp(logit) / (1 + Math.exp(logit)));
      if (score <= 29) return { level: 'success', text: `IGS II: ${score}`, detail: `Mortalité estimée: ~${Math.min(mortality, 10)}%` };
      if (score <= 40) return { level: 'warning', text: `IGS II: ${score}`, detail: `Mortalité estimée: ~${mortality}%` };
      if (score <= 52) return { level: 'warning', text: `IGS II: ${score}`, detail: `Mortalité estimée: ~${mortality}%` };
      return { level: 'danger', text: `IGS II: ${score}`, detail: `Mortalité estimée: ~${Math.min(mortality, 95)}%` };
    },
    formula: 'IGS II = Somme de 17 variables (pires valeurs des 24 premières heures)'
  }
};

// ==================== GÉRIATRIE ====================
export const GERIATRIC_SCORES = {
  mms: {
    name: 'Mini Mental State (MMS)',
    category: 'Gériatrie',
    icon: Brain,
    description: 'Évaluation cognitive globale',
    color: 'teal',
    fields: [
      { id: 'orientation_time', label: 'Orientation temporelle (année, saison, mois, jour, date)', type: 'number', min: 0, max: 5 },
      { id: 'orientation_space', label: 'Orientation spatiale (pays, région, ville, lieu, étage)', type: 'number', min: 0, max: 5 },
      { id: 'memory', label: 'Apprentissage (3 mots)', type: 'number', min: 0, max: 3 },
      { id: 'attention', label: 'Attention/Calcul (100-7 ou MONDE à l\'envers)', type: 'number', min: 0, max: 5 },
      { id: 'recall', label: 'Rappel (3 mots)', type: 'number', min: 0, max: 3 },
      { id: 'naming', label: 'Langage: Dénomination (crayon, montre)', type: 'number', min: 0, max: 2 },
      { id: 'repetition', label: 'Répétition phrase', type: 'number', min: 0, max: 1 },
      { id: 'comprehension', label: 'Compréhension (ordre en 3 temps)', type: 'number', min: 0, max: 3 },
      { id: 'reading', label: 'Lecture (fermez les yeux)', type: 'number', min: 0, max: 1 },
      { id: 'writing', label: 'Écriture phrase', type: 'number', min: 0, max: 1 },
      { id: 'praxis', label: 'Praxie constructive (copie pentagones)', type: 'number', min: 0, max: 1 }
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 30 };
    },
    interpret: (score) => {
      if (score >= 27) return { level: 'success', text: `MMS ${score}/30`, detail: 'Normal (tenir compte du niveau éducatif)' };
      if (score >= 24) return { level: 'warning', text: `MMS ${score}/30`, detail: 'Trouble cognitif léger' };
      if (score >= 19) return { level: 'warning', text: `MMS ${score}/30`, detail: 'Démence légère' };
      if (score >= 10) return { level: 'danger', text: `MMS ${score}/30`, detail: 'Démence modérée' };
      return { level: 'danger', text: `MMS ${score}/30`, detail: 'Démence sévère' };
    },
    formula: 'MMS = Orientation + Apprentissage + Attention + Rappel + Langage + Praxie'
  },

  gds15: {
    name: 'GDS-15 (Geriatric Depression Scale)',
    category: 'Gériatrie',
    icon: Brain,
    description: 'Dépistage dépression sujet âgé',
    color: 'teal',
    fields: [
      { id: 'q1', label: 'Êtes-vous satisfait de votre vie?', type: 'select', options: [{ value: 0, label: 'Oui' }, { value: 1, label: 'Non' }]},
      { id: 'q2', label: 'Avez-vous abandonné beaucoup d\'activités?', type: 'select', options: [{ value: 1, label: 'Oui' }, { value: 0, label: 'Non' }]},
      { id: 'q3', label: 'Avez-vous le sentiment que votre vie est vide?', type: 'select', options: [{ value: 1, label: 'Oui' }, { value: 0, label: 'Non' }]},
      { id: 'q4', label: 'Vous ennuyez-vous souvent?', type: 'select', options: [{ value: 1, label: 'Oui' }, { value: 0, label: 'Non' }]},
      { id: 'q5', label: 'Êtes-vous de bonne humeur la plupart du temps?', type: 'select', options: [{ value: 0, label: 'Oui' }, { value: 1, label: 'Non' }]},
      { id: 'q6', label: 'Craignez-vous qu\'il vous arrive quelque chose de mal?', type: 'select', options: [{ value: 1, label: 'Oui' }, { value: 0, label: 'Non' }]},
      { id: 'q7', label: 'Vous sentez-vous heureux la plupart du temps?', type: 'select', options: [{ value: 0, label: 'Oui' }, { value: 1, label: 'Non' }]},
      { id: 'q8', label: 'Vous sentez-vous souvent abandonné?', type: 'select', options: [{ value: 1, label: 'Oui' }, { value: 0, label: 'Non' }]},
      { id: 'q9', label: 'Préférez-vous rester chez vous plutôt que sortir?', type: 'select', options: [{ value: 1, label: 'Oui' }, { value: 0, label: 'Non' }]},
      { id: 'q10', label: 'Avez-vous plus de problèmes de mémoire que les autres?', type: 'select', options: [{ value: 1, label: 'Oui' }, { value: 0, label: 'Non' }]},
      { id: 'q11', label: 'Pensez-vous qu\'il est merveilleux de vivre?', type: 'select', options: [{ value: 0, label: 'Oui' }, { value: 1, label: 'Non' }]},
      { id: 'q12', label: 'Vous sentez-vous inutile?', type: 'select', options: [{ value: 1, label: 'Oui' }, { value: 0, label: 'Non' }]},
      { id: 'q13', label: 'Vous sentez-vous plein d\'énergie?', type: 'select', options: [{ value: 0, label: 'Oui' }, { value: 1, label: 'Non' }]},
      { id: 'q14', label: 'Pensez-vous que votre situation est désespérée?', type: 'select', options: [{ value: 1, label: 'Oui' }, { value: 0, label: 'Non' }]},
      { id: 'q15', label: 'Pensez-vous que les autres sont mieux que vous?', type: 'select', options: [{ value: 1, label: 'Oui' }, { value: 0, label: 'Non' }]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 15 };
    },
    interpret: (score) => {
      if (score <= 5) return { level: 'success', text: `GDS ${score}/15`, detail: 'Normal' };
      if (score <= 10) return { level: 'warning', text: `GDS ${score}/15`, detail: 'Dépression probable. Évaluation psychiatrique' };
      return { level: 'danger', text: `GDS ${score}/15`, detail: 'Dépression sévère probable. Avis psychiatrique urgent' };
    },
    formula: 'GDS-15: 15 questions (oui/non). Seuil: > 5 = dépression'
  },

  adl: {
    name: 'ADL de Katz',
    category: 'Gériatrie',
    icon: User,
    description: 'Autonomie activités de base',
    color: 'teal',
    fields: [
      { id: 'bathing', label: 'Se laver', type: 'select', options: [
        { value: 1, label: '1 - Indépendant' }, { value: 0, label: '0 - Dépendant' }
      ]},
      { id: 'dressing', label: 'S\'habiller', type: 'select', options: [
        { value: 1, label: '1 - Indépendant' }, { value: 0, label: '0 - Dépendant' }
      ]},
      { id: 'toileting', label: 'Aller aux toilettes', type: 'select', options: [
        { value: 1, label: '1 - Indépendant' }, { value: 0, label: '0 - Dépendant' }
      ]},
      { id: 'transfer', label: 'Se déplacer', type: 'select', options: [
        { value: 1, label: '1 - Indépendant' }, { value: 0, label: '0 - Dépendant' }
      ]},
      { id: 'continence', label: 'Continence', type: 'select', options: [
        { value: 1, label: '1 - Continent' }, { value: 0, label: '0 - Incontinent' }
      ]},
      { id: 'feeding', label: 'Se nourrir', type: 'select', options: [
        { value: 1, label: '1 - Indépendant' }, { value: 0, label: '0 - Dépendant' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 6 };
    },
    interpret: (score) => {
      if (score === 6) return { level: 'success', text: `ADL ${score}/6`, detail: 'Autonomie complète' };
      if (score >= 4) return { level: 'warning', text: `ADL ${score}/6`, detail: 'Dépendance légère' };
      if (score >= 2) return { level: 'warning', text: `ADL ${score}/6`, detail: 'Dépendance modérée' };
      return { level: 'danger', text: `ADL ${score}/6`, detail: 'Dépendance sévère' };
    },
    formula: 'ADL = Somme des 6 activités de base (toilette, habillage, WC, transfert, continence, alimentation)'
  },

  iadl: {
    name: 'IADL de Lawton',
    category: 'Gériatrie',
    icon: User,
    description: 'Autonomie activités instrumentales',
    color: 'teal',
    fields: [
      { id: 'phone', label: 'Téléphone', type: 'select', options: [
        { value: 1, label: '1 - Utilise seul' }, { value: 0, label: '0 - Aide nécessaire' }
      ]},
      { id: 'shopping', label: 'Courses', type: 'select', options: [
        { value: 1, label: '1 - Fait seul' }, { value: 0, label: '0 - Aide nécessaire' }
      ]},
      { id: 'cooking', label: 'Préparation repas', type: 'select', options: [
        { value: 1, label: '1 - Fait seul' }, { value: 0, label: '0 - Aide nécessaire' }
      ]},
      { id: 'housekeeping', label: 'Entretien maison', type: 'select', options: [
        { value: 1, label: '1 - Fait seul' }, { value: 0, label: '0 - Aide nécessaire' }
      ]},
      { id: 'laundry', label: 'Lessive', type: 'select', options: [
        { value: 1, label: '1 - Fait seul' }, { value: 0, label: '0 - Aide nécessaire' }
      ]},
      { id: 'transport', label: 'Transport', type: 'select', options: [
        { value: 1, label: '1 - Autonome' }, { value: 0, label: '0 - Aide nécessaire' }
      ]},
      { id: 'medication', label: 'Gestion médicaments', type: 'select', options: [
        { value: 1, label: '1 - Gère seul' }, { value: 0, label: '0 - Aide nécessaire' }
      ]},
      { id: 'finances', label: 'Gestion finances', type: 'select', options: [
        { value: 1, label: '1 - Gère seul' }, { value: 0, label: '0 - Aide nécessaire' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 8 };
    },
    interpret: (score) => {
      if (score === 8) return { level: 'success', text: `IADL ${score}/8`, detail: 'Autonomie instrumentale complète' };
      if (score >= 5) return { level: 'warning', text: `IADL ${score}/8`, detail: 'Perte d\'autonomie partielle' };
      return { level: 'danger', text: `IADL ${score}/8`, detail: 'Perte d\'autonomie majeure. Aide à domicile' };
    },
    formula: 'IADL = 8 activités instrumentales (téléphone, courses, repas, ménage, lessive, transport, médicaments, finances)'
  },

  mna: {
    name: 'MNA (Mini Nutritional Assessment)',
    category: 'Gériatrie',
    icon: Scale,
    description: 'Évaluation état nutritionnel',
    color: 'teal',
    fields: [
      { id: 'appetite', label: 'Appétit diminué?', type: 'select', options: [
        { value: 0, label: '0 - Anorexie sévère' }, { value: 1, label: '1 - Anorexie modérée' }, { value: 2, label: '2 - Pas de problème' }
      ]},
      { id: 'weightLoss', label: 'Perte de poids', type: 'select', options: [
        { value: 0, label: '0 - > 3 kg' }, { value: 1, label: '1 - Ne sait pas' },
        { value: 2, label: '2 - 1-3 kg' }, { value: 3, label: '3 - Pas de perte' }
      ]},
      { id: 'mobility', label: 'Mobilité', type: 'select', options: [
        { value: 0, label: '0 - Lit/fauteuil' }, { value: 1, label: '1 - Autonome intérieur' }, { value: 2, label: '2 - Sort du domicile' }
      ]},
      { id: 'stress', label: 'Stress psychologique ou maladie aiguë', type: 'select', options: [
        { value: 0, label: '0 - Oui' }, { value: 2, label: '2 - Non' }
      ]},
      { id: 'neuropsych', label: 'Problèmes neuropsychologiques', type: 'select', options: [
        { value: 0, label: '0 - Démence/dépression sévère' }, { value: 1, label: '1 - Démence légère' }, { value: 2, label: '2 - Pas de problème' }
      ]},
      { id: 'bmi', label: 'IMC', type: 'select', options: [
        { value: 0, label: '0 - < 19' }, { value: 1, label: '1 - 19-20.9' },
        { value: 2, label: '2 - 21-22.9' }, { value: 3, label: '3 - ≥ 23' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 14 };
    },
    interpret: (score) => {
      if (score >= 12) return { level: 'success', text: `MNA ${score}/14`, detail: 'État nutritionnel normal' };
      if (score >= 8) return { level: 'warning', text: `MNA ${score}/14`, detail: 'Risque de malnutrition. Compléments nutritionnels' };
      return { level: 'danger', text: `MNA ${score}/14`, detail: 'Malnutrition. Prise en charge nutritionnelle' };
    },
    formula: 'MNA Short-Form: 6 items (dépistage)'
  }
};

// Export all extended scores
export const EXTENDED_SCORES = {
  ...EMERGENCY_SCORES,
  ...NEUROLOGY_EXTENDED_SCORES,
  ...PULMONOLOGY_EXTENDED_SCORES,
  ...ICU_EXTENDED_SCORES,
  ...GERIATRIC_SCORES
};
